import { WatchQueryFetchPolicy, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Queries } from '@shared/api';

export function useListWorkflowTemplates({
  skip = false,
  input = {},
  loadAll = false,
  fetchPolicy = 'network-only',
}: {
  skip?: boolean;
  input?: Partial<MBAPI.ListWorkflowTemplates>;
  loadAll?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}): {
  workflowTemplates?: MBAPI.WorkflowTemplate[];
  loading: boolean;
  error?: Error;
  hasMore?: boolean;
  loadMore?(): void;
} {
  const [isLoadingMore, setisLoadingMore] = useState(false);
  const [offsetUpdated, setOffsetUpdated] = useState(false);
  const variables = useMemo(
    () => ({
      input: {
        isPublic: false,
        ...input,
      },
    }),
    [input],
  );
  const { data, loading, error, fetchMore } = useQuery<
    MBAPI.ListWorkflowTemplatesV2Query,
    MBAPI.ListWorkflowTemplatesV2QueryVariables
  >(Queries.workflow.listWorkflowTemplatesV2, {
    variables,
    skip,
    fetchPolicy,
  });
  const offset = useMemo(() => {
    setOffsetUpdated(true);
    return data?.listWorkflowTemplatesV2?.workflowTemplates?.length ?? 0;
  }, [data?.listWorkflowTemplatesV2?.workflowTemplates?.length]);

  const workflowTemplates = data?.listWorkflowTemplatesV2?.workflowTemplates as
    | MBAPI.WorkflowTemplate[]
    | undefined;
  const hasMore = data?.listWorkflowTemplatesV2?.hasMore;

  const loadMore = useCallback(() => {
    if (hasMore && !loading && !isLoadingMore && offsetUpdated) {
      setOffsetUpdated(false);
      setisLoadingMore(true);
      void fetchMore({
        variables: {
          input: {
            ...variables.input,
            offset: offset,
          },
        },
      }).finally(() => setisLoadingMore(false));
    }
  }, [offset, fetchMore, variables, hasMore, isLoadingMore, loading, offsetUpdated]);

  useEffect(() => {
    if (loadAll && hasMore && !loading && !isLoadingMore && offsetUpdated) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadAll, loading, loadMore, offsetUpdated]);

  return useMemo(
    () => ({ workflowTemplates, loading, error, hasMore, loadMore }),
    [workflowTemplates, loading, error, hasMore, loadMore],
  );
}
